import React from 'react'
import { storiesOf } from '@storybook/react'
import { withKnobs, boolean } from '@storybook/addon-knobs/react'
import { action } from '@storybook/addon-actions'

import { UserLayerProvider } from 'components/UserLayer'
import FriendsScreen from 'routes/Friends/FriendsScreen'

import {
  FriendsResponse,
  UserListResponse,
  User,
  UserRelationship,
  GeneralResponse,
  UserInfoResponse,
  ReferralsResponse,
  Money,
  ReferralBonus,
  Referral
} from 'protos'
import { MemoryRouter } from 'react-router'
import Chance from 'chance'

import { fakeApiCall } from './utils'

// fake data

const chance = new Chance(0)

const randomUser = (options?: Object) =>
  User.fromObject({
    userId: chance.guid(),
    name: chance.name(),
    photoUrl: `http://i.pravatar.cc/128?img=${chance.integer({ min: 1, max: 70 })}`,
    relationship: UserRelationship.UR_NONE,
    ...options,
  })

const fakeUsers = [
  randomUser({ relationship: UserRelationship.UR_OUTGOING }),
  randomUser({ relationship: UserRelationship.UR_OUTGOING }),
  randomUser({ relationship: UserRelationship.UR_MUTUAL }),
  randomUser({ facebookFriend: true }),
  randomUser({ facebookFriend: true }),
  randomUser({ relationship: UserRelationship.UR_INCOMING }),
  randomUser({ facebookFriend: true }),
  randomUser({ relationship: UserRelationship.UR_MUTUAL }),
]

const fakeFetchFriends = (requests: User[], friends: User[], isFacebookConnected: boolean, hasError?: boolean) =>
  fakeApiCall(
    FriendsResponse.fromObject({
      request: requests,
      friend: friends,
      facebookCount: friends.filter(friend => friend.facebookFriend).length,
      facebookStatus: isFacebookConnected
        ? FriendsResponse.FacebookStatus.FBS_SUCCESS
        : FriendsResponse.FacebookStatus.FBS_NO_TOKEN
    }),
    500,
    hasError ? 'Could not retrieve data from the server.' : undefined
  )

const fakeSearch = (text: string, hasError?: boolean) => {
  const response = UserListResponse.fromObject({ user: text.length >= 3 ? fakeUsers : [] })

  return fakeApiCall(response, 500, hasError ? 'Could not retrieve data from the server.' : undefined)
}

const fakeFacebookLogin = () => {
  action('loginToFacebook')()
  return Promise.resolve()
}

const fakeFetchReferrals = (hasRefers: boolean, hasError?: boolean) => {
  const response = ReferralsResponse.fromObject({
    bonus: [
      ReferralBonus.fromObject({
        type: ReferralBonus.Type.RBT_REGISTRATION,
        value: Money.fromObject({ value: 50 }),
      }),
      ReferralBonus.fromObject({
        type: ReferralBonus.Type.RBT_MATCHES_10,
        value: Money.fromObject({ value: 250 }),
      }),
      ReferralBonus.fromObject({
        type: ReferralBonus.Type.RBT_MATCHES_100,
        value: Money.fromObject({ value: 1000 }),
      }),
      ReferralBonus.fromObject({
        type: ReferralBonus.Type.RBT_PURCHASE,
        value: Money.fromObject({ value: 1000 }),
      }),
    ],
  })

  if (hasRefers) {
    response.bonus[0].receivedBonus = Money.fromObject({ value: 50 })
    response.bonus[0].referral = [
      Referral.fromObject({
        user: randomUser(),
        nextRemaining: 10,
        nextBonus: Money.fromObject({ value: 250 }),
      }),
    ]

    response.referredUsers = 1
    response.receivedBonus = Money.fromObject({ value: 50 })
  }

  return fakeApiCall(response, 500, hasError ? 'Could not retrieve data from the server.' : undefined)
}

const responseOK = GeneralResponse.fromObject({ result: GeneralResponse.Result.GR_SUCCESS })
const getUserInfo = (userId: string) => UserInfoResponse.fromObject({
  user: fakeUsers.find(user => user.userId === userId),
})

// stories

storiesOf('FriendsScreen', module)
  .addDecorator(withKnobs)
  .add('default', () => {
    const hasFriends = boolean('hasFriends', false)
    const hasFacebookFriends = boolean('hasFacebookFriends', false)
    const isFacebookConnected = boolean('isFacebookConnected', false)
    const hasRefers = boolean('wonBonus', false)
    const badServer = boolean('badServer', false)

    const requests = fakeUsers
      .filter(friend => hasFriends && friend.relationship === UserRelationship.UR_OUTGOING)

    const friends = fakeUsers
      .filter(friend => (
        friend.relationship !== UserRelationship.UR_OUTGOING
        && (hasFriends || friend.facebookFriend)
        && ((isFacebookConnected && hasFacebookFriends) || !friend.facebookFriend)
      ))

    return (
      <MemoryRouter initialEntries={['/friends']}>
        <UserLayerProvider
          getUserInfo={(userId: string) => Promise.resolve(getUserInfo(userId))}
          addFriend={(userId: string) => Promise.resolve(responseOK)}
          removeFriend={(userId: string) => Promise.resolve(responseOK)}
          onChallenge={action('onChallenge')}
        >
          <FriendsScreen
            key={
              Number(hasFriends)
              + 2 * Number(hasFacebookFriends)
              + 4 * Number(isFacebookConnected)
              + 8 * Number(hasRefers)
              + 16 * Number(badServer)
            }
            getUserFriends={() => fakeFetchFriends(requests, friends, isFacebookConnected, badServer)}
            getUserReferrals={() => fakeFetchReferrals(hasRefers, badServer)}
            loginToFacebook={fakeFacebookLogin}
            searchUsers={(text: string) => fakeSearch(text, badServer)}
            onChallenge={action('onChallenge')}
          />
        </UserLayerProvider>
      </MemoryRouter>
    )
  })
